import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { logger } from '@core/utils';
import type { CompanySummary } from '@core/types/api';

/**
 * localStorage key holding the active company uuid.
 *
 * The base query mirrors this key (`ACTIVE_COMPANY_STORAGE_KEY`) as a fallback when
 * it has to build the `X-Company` header before the store is rehydrated. The two MUST
 * stay in sync.
 */
export const ACTIVE_COMPANY_KEY = 'activeCompanyUuid';

export interface CompanyState {
  activeCompanyUuid: string | null;
}

/** Read the active company uuid from localStorage, best effort. */
export const loadActiveCompanyFromStorage = (): string | null => {
  try {
    const current = localStorage.getItem(ACTIVE_COMPANY_KEY);
    if (current) return current;
    const legacy = localStorage.getItem('activeSiteUuid');
    if (legacy) {
      localStorage.setItem(ACTIVE_COMPANY_KEY, legacy);
      localStorage.removeItem('activeSiteUuid');
    }
    return legacy;
  } catch (error) {
    logger.error('Error loading active company from localStorage:', error);
    return null;
  }
};

const persistActiveCompany = (uuid: string): void => {
  try {
    localStorage.setItem(ACTIVE_COMPANY_KEY, uuid);
  } catch (error) {
    logger.error('Error saving active company to localStorage:', error);
  }
};

const clearPersistedActiveCompany = (): void => {
  try {
    localStorage.removeItem(ACTIVE_COMPANY_KEY);
    localStorage.removeItem('activeSiteUuid');
  } catch (error) {
    logger.error('Error clearing active company from localStorage:', error);
  }
};

/**
 * Pick the company a session should open on: the organization's default company, then
 * the first active one, then the first one listed. `null` when the list is empty.
 *
 * Shared by {@link useActiveCompany} and the tests so both agree on what "the
 * default company" means on the client.
 */
export const preferredCompanyUuid = (companies: CompanySummary[]): string | null =>
  companies.find((company) => company.is_default)?.uuid ??
  companies.find((company) => company.is_active)?.uuid ??
  companies[0]?.uuid ??
  null;

/**
 * Company slice: which company (company) of the active organization the user is looking at.
 *
 * A company is a filter inside the tenant, never a boundary (ADR-0018): the backend
 * falls back to the default company when the header is absent, so `null` here is a
 * valid, working state. Rehydrated at module load so the first request already
 * carries the right `X-Company` header; persistence lives in the reducer.
 */
const companySlice = createSlice({
  name: 'company',
  initialState: (): CompanyState => ({
    activeCompanyUuid: loadActiveCompanyFromStorage(),
  }),
  reducers: {
    initializeActiveCompany: (state) => {
      state.activeCompanyUuid = loadActiveCompanyFromStorage();
    },
    setActiveCompany: (state, action: PayloadAction<string>) => {
      state.activeCompanyUuid = action.payload;
      persistActiveCompany(action.payload);
    },
    /**
     * Forget the selection (organization or user switch, or a company the backend
     * refused). The next request goes out without `X-Company`, which the backend
     * reads as the default company, and `useActiveCompany` re-selects from the list.
     */
    clearActiveCompany: (state) => {
      state.activeCompanyUuid = null;
      clearPersistedActiveCompany();
    },
  },
});

export const { initializeActiveCompany, setActiveCompany, clearActiveCompany } = companySlice.actions;

export default companySlice.reducer;

// Selectors. Typed loosely against the slice shape so they can be reused before
// RootState is constructed (avoids a circular import on the store).
interface WithCompany {
  company: CompanyState;
}

export const selectActiveCompanyUuid = (state: WithCompany): string | null => state.company.activeCompanyUuid;
